import { AlertTriangle, UserMinus } from 'lucide-react'
import React, { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { useAuth } from '@/contexts/auth-context'
import { useChat } from '@/contexts/chat-context'

import { Conversation, ParticipantRole } from '@/types/chat.type'

interface RemoveParticipantsDialogProps {
  conversation: Conversation
  onClose: () => void
}

export const RemoveParticipantsDialog: React.FC<RemoveParticipantsDialogProps> = ({ conversation, onClose }) => {
  const { user } = useAuth()
  const { removeParticipants, reloadConversations } = useChat()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<number[]>([])

  // Add null checks to prevent TypeError
  if (!conversation || !conversation.participants) {
    return null
  }

  // Filter out current user and owner from removal list
  const removableParticipants = conversation.participants.filter(
    participant => participant.user.id !== user?.id && participant.role !== ParticipantRole.OWNER
  )

  const handleRemoveParticipants = async () => {
    if (selectedParticipantIds.length === 0) return

    setIsLoading(true)
    try {
      // Map participant IDs to user IDs for the API call
      const userIds = selectedParticipantIds
        .map(participantId => {
          const participant = conversation.participants.find(p => p.id === participantId)
          return participant?.user.id
        })
        .filter((id): id is number => id !== undefined)

      await removeParticipants(conversation.id, userIds)
      setSelectedParticipantIds([])

      // Refresh conversations to show updated participant list
      await reloadConversations()

      onClose()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to remove participants:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleParticipantSelection = (participantId: number) => {
    setSelectedParticipantIds(prev =>
      prev.includes(participantId) ? prev.filter(id => id !== participantId) : [...prev, participantId]
    )
  }

  const isRemoveDisabled = selectedParticipantIds.length === 0 || isLoading

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserMinus className="h-5 w-5" />
            Remove Participants
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning */}
          <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm text-yellow-800">
              Removing participants will immediately remove them from the conversation.
            </span>
          </div>

          {/* Participants List */}
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {removableParticipants.map(participant => (
              <div key={participant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-red-600">
                      {participant.user.firstName[0]}
                      {participant.user.lastName[0]}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium">
                      {participant.user.firstName} {participant.user.lastName}
                    </div>
                    <div className="text-sm text-gray-500 capitalize">{participant.role}</div>
                  </div>
                </div>
                <Button
                  variant={selectedParticipantIds.includes(participant.id) ? 'destructive' : 'outline'}
                  size="sm"
                  onClick={() => toggleParticipantSelection(participant.id)}
                >
                  {selectedParticipantIds.includes(participant.id) ? 'Selected' : 'Select'}
                </Button>
              </div>
            ))}
            {removableParticipants.length === 0 && (
              <div className="text-center text-gray-500 py-4">No participants can be removed</div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveParticipants}
              disabled={isRemoveDisabled}
              className="flex-1"
            >
              {isLoading
                ? 'Removing...'
                : `Remove ${selectedParticipantIds.length} Participant${selectedParticipantIds.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
